/**
 * SYNERGY VALUE CAPTURE VIEWS (founder GO 2026-09-26 11:04 PT; lead rulings 11:24 PT).
 *
 * Opened by canvas buttons only ("Value capture", "Synergy bridge", and a deal
 * switch): a fixed, server-authored plan runs through the governed engine with
 * no model reading in the path, every figure is published through the same
 * binding fence as an /ask answer, and every panel is an existing declared
 * exhibit (KPI strip, decision table, bridge). The canvas draws a panel exactly
 * as it draws an answer's exhibit.
 *
 * Stages, from the dataset's own definitions (phoenix_deal_commitment_lines):
 * - announced: target run-rate, the deal's announced promise set, base band;
 * - forecast: planned value (the current plan) at the latest plan point;
 * - validated: NOT YET TRACKED (decisions nest and carry no root marker yet);
 * - realized: actual value at the latest as-of that carries one.
 */
import type { AskHelixNumericBindingDto } from '../../ask-helix';
import type { AskDeclaredExhibitDto } from './declared-exhibit';
import type { AskStorySegment } from './declared-storyline';
import type { ReconciledBridgeWire } from '../../governed-reconciliation';

export const SYNERGY_VALUE_STAGES = ['announced', 'forecast', 'validated', 'realized'] as const;
export type SynergyValueStage = (typeof SYNERGY_VALUE_STAGES)[number];

/** One stage's governed state: tracked with its own as-of, or shown muted with no number. */
export type SynergyStageStatus = Readonly<{
  stage: SynergyValueStage;
  label: string;
  tracked: boolean;
  /** What the stage's figures are as of, in reader words; null when not tracked. */
  asOf: string | null;
  /** The KPI figure's binding placeholder; null when not tracked. */
  figure: string | null;
}>;

/** A deal the view can show; `dealRef` is the governed deal member ref. */
export type SynergyViewDeal = Readonly<{ dealRef: string; label: string }>;

/** Coverage of all commitments, with the additive subset and excluded overlays explicit. */
export type SynergyForecastCoverage = Readonly<{
  forecastable: number;
  total: number;
  includedForecastable: number;
  excluded: readonly string[];
  missing: readonly Readonly<{
    commitmentRef: string;
    label: string;
    reasonCode: 'missing_anchor' | 'missing_band' | 'horizon_unavailable';
    reason: string;
  }>[];
}>;

/** One panel: a declared exhibit over its own published, engine-bound figures. */
export type SynergyViewPanel = Readonly<{
  panelId: string;
  title: string;
  declaredExhibit: AskDeclaredExhibitDto;
  numericBindings: readonly AskHelixNumericBindingDto[];
  /** One plain sentence under the panel, or null. */
  note: string | null;
  coverage?: SynergyForecastCoverage;
  /** Exact server computation; the client never derives this check from displayed numbers. */
  reconciledBridge?: ReconciledBridgeWire;
  /** Server-authored statement about the displayed endpoints, change and steps. */
  reconciliationNote?: string;
}>;

export type SynergyValueCaptureView = Readonly<{
  schemaVersion: 'synergy_value_capture_view_v1';
  deal: SynergyViewDeal;
  deals: readonly SynergyViewDeal[];
  stages: readonly SynergyStageStatus[];
  panels: readonly SynergyViewPanel[];
  /**
   * The engine's plan change vs committed over the same commitments (the bridge's net
   * change), published in the panels' bindings and placed in no exhibit, for a headline
   * to name; null when the engine could not read it.
   */
  planChange: string | null;
  /**
   * The advisor band over the stage strip (UI agent 2026-09-27): only what the strip does
   * not show. When the engine's plan change is a reduction, `next` is the reviewed next step
   * (it places `planChange`, once) and `why` says in words what that means; null otherwise.
   */
  headline: SynergyValueCaptureHeadline | null;
  /** What changed since the month before the latest reported one; null when there is no earlier month. */
  sinceLastMonth: SynergySinceLastMonth | null;
}>;

/** "Recommended next step" and "Why", from typed findings through reviewed grammars only. */
export type SynergyValueCaptureHeadline = Readonly<{
  next: readonly AskStorySegment[];
  why: string;
}>;

/** One signal whose status changed month over month, in governed words (never refs). */
export type SynergyTurnedSignal = Readonly<{
  commitmentRef?: string;
  commitment: string;
  signal: string;
  /** Source status code for the prior row, when present. */
  beforeCode?: string | null;
  before: string;
  /** Source status code for the current row, when present. */
  afterCode?: string | null;
  after: string;
  /** The typed name of what the signal waits on, or null. */
  dependency: string | null;
  /** The source ref for the dependency, when the row publishes one. */
  dependencyRef?: string | null;
  /** Planned milestone date, `YYYY-MM-DD`, when supplied by the signal row. */
  plannedDate?: string | null;
  /** Forecast milestone date, `YYYY-MM-DD`, when supplied by the signal row. */
  forecastDate?: string | null;
  /** Source late-day measure. Its unit is whatever the source row declares; null means missing. */
  daysLate?: string | null;
}>;

export type SynergyMonthlyRunRate = Readonly<{
  beforeLabel: string;
  nowLabel: string;
  beforeFigure: string | null;
  nowFigure: string | null;
  changes: readonly Readonly<{
    commitmentRef: string;
    label: string;
    figure: string | null;
  }>[];
  numericBindings: readonly AskHelixNumericBindingDto[];
  unavailableReason: string | null;
}>;

/**
 * "Since last month" (lead ruling 2026-09-26 19:39 PT): the latest reported month against
 * the one before. The plan is left out on purpose: it ramps monthly, so its month-over-month
 * move is schedule, not change.
 */
export type SynergySinceLastMonth = Readonly<{
  /** Always names both as-of dates, e.g. "Since last month (Aug 31 vs Jul 31, 2026)". */
  label: string;
  /** The two as-ofs compared, in reader words (e.g. "Jul 31, 2026" and "Aug 31, 2026"). */
  asOf: Readonly<{ before: string; now: string }>;
  /** Engine differences (this month minus last) as a KPI panel; its note carries the captions. */
  panel: SynergyViewPanel | null;
  /**
   * The governing sentence. `countFigure`, when present, is the engine count of the signals
   * that turned late, and the text names it as the token `{count}`.
   */
  headline: Readonly<{ text: string; countFigure: string | null }> | null;
  turned: readonly SynergyTurnedSignal[];
  /** Why part of the section could not be computed, in the engine's words; null when all of it was. */
  unavailableReason: string | null;
  /** The panel's realized run-rate change (a placeholder in its bindings), for a rail finding. */
  realizedChange?: string | null;
  /** Per-commitment realized run-rate movement for the approved waterfall. */
  monthlyRunRate?: SynergyMonthlyRunRate | null;
  /** The one dependency every signal that went late waits on, or null. */
  lateDependency?: string | null;
  /** The source ref shared by those same late signals; never derived from their labels. */
  lateDependencyRef?: string | null;
  /** Signals reported last month with no row this month (not reported now), beside `turned`. */
  withdrawn?: readonly SynergyTurnedSignal[];
}>;

export type SynergyBridgeView = Readonly<{
  schemaVersion: 'synergy_bridge_view_v1';
  deal: SynergyViewDeal;
  deals: readonly SynergyViewDeal[];
  /** The announced total to the current forecast, a step per moved commitment; null when it cannot reconcile. */
  panel: SynergyViewPanel | null;
  /** Why there is no bridge, in plain words; null when there is one. */
  unavailableReason: string | null;
}>;

/** The read-only routes the canvas buttons call (GET, `?deal=<dealRef>`; no deal = the first). */
export const SYNERGY_VIEW_PATHS = {
  valueCapture: 'v1/views/synergy/value-capture',
  bridge: 'v1/views/synergy/bridge',
} as const;
